import { useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ReactComponent as Logo } from '../censuszen-logo.svg';
import '../styles/LandingPage.css';

/* SVG icons as small components to keep JSX readable */
const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M3.5 8H12.5M12.5 8L8.5 4M12.5 8L8.5 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="3.5 8 6.5 11 12.5 5"/>
  </svg>
);

const FileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
  </svg>
);

const ClipboardIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/>
  </svg>
);

const FlaskIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M9 3h6v5.586a1 1 0 01-.293.707L11.293 12.7a1 1 0 00-.293.707V19l-2 2v-7.586a1 1 0 00-.293-.707L5.293 9.293A1 1 0 015 8.586V3"/>
  </svg>
);

const DocIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

/* Animated demo — manages the step-label active class via useEffect */
function HeroDemo() {
  const uploadRef = useRef(null);
  const extractRef = useRef(null);
  const reviewRef = useRef(null);

  const runCycle = useCallback(() => {
    const labels = [uploadRef.current, extractRef.current, reviewRef.current];
    const clear = () => labels.forEach(l => l?.classList.remove('active'));

    clear();
    uploadRef.current?.classList.add('active');

    const t1 = setTimeout(() => { clear(); extractRef.current?.classList.add('active'); }, 3500);
    const t2 = setTimeout(() => { clear(); reviewRef.current?.classList.add('active'); }, 7500);

    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  useEffect(() => {
    let cleanup = runCycle();
    const interval = setInterval(() => {
      cleanup();
      cleanup = runCycle();
    }, 12000);
    return () => { cleanup(); clearInterval(interval); };
  }, [runCycle]);

  return (
    <div className="hero-demo">
      <div className="demo-window">
        <div className="demo-titlebar">
          <span className="demo-dot" />
          <span className="demo-dot" />
          <span className="demo-dot" />
          <span className="demo-titlebar-text">CensusZen &mdash; Case #4271</span>
        </div>

        <div className="demo-body">
          {/* Progress pips */}
          <div className="demo-steps">
            <div className="demo-step-pip" />
            <div className="demo-step-pip" />
            <div className="demo-step-pip" />
          </div>
          <div className="demo-step-labels">
            <span className="demo-step-label" ref={uploadRef}>Upload</span>
            <span className="demo-step-label" ref={extractRef}>Extract</span>
            <span className="demo-step-label" ref={reviewRef}>Review</span>
          </div>

          {/* Scan line */}
          <div className="extract-scan" />

          {/* Phase 1: Documents uploading */}
          <div className="demo-phase demo-upload">
            <div className="doc-card">
              <div className="doc-icon doc-icon--pdf"><FileIcon /></div>
              <div className="doc-meta">
                <div className="doc-name">Discharge_Summary.pdf</div>
                <div className="doc-detail">2.4 MB &middot; 12 pages</div>
              </div>
              <div className="doc-check"><CheckIcon /></div>
            </div>
            <div className="doc-card">
              <div className="doc-icon doc-icon--clinical"><ClipboardIcon /></div>
              <div className="doc-meta">
                <div className="doc-name">Clinical_Notes_Jan.pdf</div>
                <div className="doc-detail">1.1 MB &middot; 8 pages</div>
              </div>
              <div className="doc-check"><CheckIcon /></div>
            </div>
            <div className="doc-card">
              <div className="doc-icon doc-icon--lab"><FlaskIcon /></div>
              <div className="doc-meta">
                <div className="doc-name">Lab_Results_Recent.pdf</div>
                <div className="doc-detail">680 KB &middot; 4 pages</div>
              </div>
              <div className="doc-check"><CheckIcon /></div>
            </div>
          </div>

          {/* Phase 2: Data extraction */}
          <div className="demo-phase demo-extract">
            <div className="extract-grid">
              <div className="extract-field">
                <div className="extract-label">Patient</div>
                <div className="extract-value">Margaret Chen, 72</div>
              </div>
              <div className="extract-field">
                <div className="extract-label">Primary Dx</div>
                <div className="extract-value extract-value--highlight">L Hip Fracture</div>
              </div>
              <div className="extract-field">
                <div className="extract-label">Admission</div>
                <div className="extract-value">01/15/2026</div>
              </div>
              <div className="extract-field">
                <div className="extract-label">Insurance</div>
                <div className="extract-value">Medicare A</div>
              </div>
              <div className="extract-field">
                <div className="extract-label">Functional Status</div>
                <div className="extract-value">Mod Assist &times; 2</div>
              </div>
              <div className="extract-field">
                <div className="extract-label">Prior Level</div>
                <div className="extract-value extract-value--highlight">Independent</div>
              </div>
            </div>
          </div>

          {/* Phase 3: HPI + Approval */}
          <div className="demo-phase demo-approve">
            <div className="approve-hpi">
              <div className="approve-hpi-header">
                <div className="approve-hpi-icon"><DocIcon /></div>
                <span className="approve-hpi-title">Generated HPI</span>
              </div>
              <div className="approve-hpi-lines">
                <div className="hpi-line" />
                <div className="hpi-line" />
                <div className="hpi-line" />
                <div className="hpi-line" />
                <div className="hpi-line" />
              </div>
            </div>
            <div className="approve-badge">
              <div className="approve-badge-check"><CheckIcon size={16} /></div>
              <div>
                <div className="approve-badge-text">Ready for Review</div>
                <div className="approve-badge-sub">Census complete &middot; 2m 14s</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LandingPage() {
  return (
    <div className="landing-page">
      {/* --- Navigation --- */}
      <nav className="landing-nav">
        <Logo className="landing-nav-logo" aria-label="CensusZen" />
        <div className="landing-nav-links">
          <Link to="/faq" className="landing-nav-link landing-nav-link--ghost">
            FAQ
          </Link>
          <Link to="/login" className="landing-nav-link landing-nav-link--ghost">
            Log in
          </Link>
          <Link to="/register" className="landing-nav-link landing-nav-link--solid">
            Sign Up
          </Link>
        </div>
      </nav>

      {/* --- Hero --- */}
      <section className="hero">
        <div className="hero-copy">
          <h1 className="hero-headline">
            Patient approvals<br />in minutes, <em>not hours</em>
          </h1>
          <p className="hero-subtitle">
            CensusZen reads your clinical documents, extracts every important detail and generates
            complete HPIs and approval documents so your team can focus on getting
            patients the care they need.
          </p>
          <div className="hero-actions">
            <Link to="/request-access" className="btn-primary">
              Request Early Access <ArrowIcon />
            </Link>
            <Link to="/login" className="btn-ghost">Sign In</Link>
          </div>
          <div className="hero-proof">
            <div className="hero-stat">
              <div className="hero-stat-number">3 min</div>
              <div className="hero-stat-label">Avg. processing time</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <div className="hero-stat-number">29</div>
              <div className="hero-stat-label">Fields auto-extracted</div>
            </div>
            <div className="hero-stat-divider" />
            <div className="hero-stat">
              <div className="hero-stat-number">100%</div>
              <div className="hero-stat-label">Human-reviewed output</div>
            </div>
          </div>
        </div>

        <HeroDemo />
      </section>

      {/* --- Final CTA --- */}
      <section className="landing-cta-section">
        <div className="landing-cta-card">
          <h2>Ready to streamline your census?</h2>
          <p>
            Join clinical teams already saving hours on documentation every week.
          </p>
          <div className="landing-cta-buttons">
            <Link to="/request-access" className="landing-cta-primary">
              Request Early Access
            </Link>
            <Link to="/register" className="landing-cta-secondary">
              Have an invite code?
            </Link>
          </div>
        </div>
      </section>

      {/* --- Footer --- */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy">Privacy Policy</Link>
          <span className="landing-footer-divider">&middot;</span>
          <Link to="/accept-tos">Terms of Service</Link>
          <span className="landing-footer-divider">&middot;</span>
          <Link to="/faq">FAQ</Link>
        </div>
        &copy; {new Date().getFullYear()} CensusZen. All rights reserved.
      </footer>
    </div>
  );
}
